import { checkFullPath } from "../../utils";

export const homePageStructuredData = (
  currentURL: string,
  metaTitle: string,
  metaDescription: string,
  newDate: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: currentURL,
    name: metaTitle,
    isPartOf: "AARP Games",
    description: metaDescription,
    publisher: {
      "@type": "Organization",
      name: "AARP",
      description:
        "AARP is a nonprofit, nonpartisan organization that empowers people to choose how they live as they age.",
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.aarp.net/content/dam/aarp/graphics/aarp_logos/aarp-white-logo-footer.svg",
      },
      sameAs: [
        "https://www.facebook.com/AARP/",
        "https://twitter.com/AARP/",
        "https://www.instagram.com/aarp/",
        "https://www.linkedin.com/company/aarp/",
      ],
    },
    dateModified: newDate,
  };
};

export const helpPageStructuredData = (
  currentURL: string,
  metaTitle: string,
  metaDescription: string,
  newDate: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    additionalType: "WebPage",
    url: currentURL,
    name: metaTitle,
    isPartOf: "AARP Games",
    description: metaDescription,
    mainEntity: [
      {
        "@type": "Question",
        name: "How do I change the size of the game area?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "By default, the game starts in the larger screen size mode. To toggle to standard size, click on the square icon on the bottom right hand corner of the game screen. To toggle back up to large size, click on the square icon again. This feature is not available on smartphones.",
        },
      },
      {
        "@type": "Question",
        name: "Can I play AARP Games on my mobile phone or tablet?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "All AARP games are built using WebGL technology, which is accessible on all devices – desktop computers, laptops, and most Android and iOS mobile phones and tablets. At this time Amazon Kindle Fire and the Barnes & Noble Nook tablets are not supported. Please see full system requirements for detailed information.",
        },
      },
      {
        "@type": "Question",
        name: "What are the system requirements to play AARP Games?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "<p>For the most enjoyable experience when playing AARP Games, make sure your computer/device of choice supports the minimum system requirements as follows:</p><p>Hardware requirements:</p><ul><li>1.5 GHz processor</li><li>Windows OS: Windows 10</li><li>macOS: Mojave (v10.14)</li><li>Resolution: 1024x768</li><li>2 Mb/s internet connection</li><li>JavaScript enabled</li></ul><p>Desktop browsers:</p><ul><li>Chrome 93</li><li>Edge 93</li><li>Firefox 91</li><li>Safari 13</li></ul><p>Mobile devices browsers:</p><ul><li>Chrome 90</li><li>Safari 13</li></ul><p>Mobile devices OS:</p><ul><li>iOS 13</li><li>Android 9</ul>",
        },
      },
      {
        "@type": "Question",
        name: "How do I find the instructions on a game?",
        acceptedAnswer: {
          "@type": "Answer",
          text: 'On many of the AARP’s games, instructions are in a menu rather than on the opening screen. Look in the corners of the game screen for a question mark icon, a settings icon (looks like a gear), an "i" information icon or a menu icon (three horizontal line stacked together or two vertical lines stacked together).',
        },
      },
      {
        "@type": "Question",
        name: "How do I save my score?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You may save your score to the personal score box and leaderboard as long as you are registered and logged in to AARP.org.",
        },
      },
      {
        "@type": "Question",
        name: "How do I log in?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "<p>You will be asked if you want to save your score at the end of the game and prompted to log in. You can also find the Login prompt in the top right of the red header bar. Type in the email address and password you used to register with AARP.org and select 'Log In'.</p><p>You can confirm that you are logged in by looking for your name in on the right side of the red header bar at the top of the page.</p>",
        },
      },
      {
        "@type": "Question",
        name: "How do I register on AARP.org?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If you aren’t yet registered on AARP.org, you may find the “Register” prompt in the top right of the red header bar. You do not have to be an AARP member to register on AARP.org and save your scores.",
        },
      },
      {
        "@type": "Question",
        name: "What if I forgot my password?",
        acceptedAnswer: {
          "@type": "Answer",
          text: 'If you don\'t remember your AARP.org password, click "Forgot your password?" on the login page and follow the prompts to reset it.',
        },
      },
      {
        "@type": "Question",
        name: "My game isn’t loading. What can I do?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "<p>You may need to clear your cookies and update your browser to the latest version.</p><p>Instruction to clear your cookies and browser cache:</p><ul><li>Chrome: https://support.google.com/accounts/answer/32050</li><li>FireFox: https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox</li><li>Edge: https://support.microsoft.com/en-us/help/4468242/microsoft-edge-browsing-data-and-privacy-microsoft-privacy</li>			<li>Safari: https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac</li><li>Safari (iPhone, iPad, iPod Touch): https://support.apple.com/en-us/HT201265</li></ul><p>Instructions to update your browser:</p>		<ul><li>Chrome: https://support.google.com/chrome/answer/95414</li><li>FireFox: https://support.mozilla.org/en-US/kb/update-firefox-latest-version</li><li>Edge: https://support.microsoft.com/en-us/help/4027667/windows-10-update</li><li>Safari: https://support.apple.com/en-us/HT204416</li></ul>",
        },
      },
      {
        "@type": "Question",
        name: "My games seem slow. What can I do?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If your browser is up to date and your cookies are cleared, the problem may be related to the speed of your internet connection. You can check your internet speed here: http://www.speedtest.net . If you speed is less than 2 MB/s, please contact your internet service provider. They should help you investigate your connectivity issues.",
        },
      },
      {
        "@type": "Question",
        name: "What is the AARP Rewards on Games category?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The AARP Rewards category includes games that qualify for earning AARP Rewards points. AARP Rewards is AARP’s loyalty program that rewards you for a life well played. You can earn points by completing certain activities throughout AARP.org.",
        },
      },
      {
        "@type": "Question",
        name: "Who can earn AARP Rewards points on Games?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Anyone who is an active AARP Rewards participant can qualify to earn points. Not part of AARP Rewards yet? Get started for free. To access AARP Members only games, including Let’s Crossword, you will need an AARP Membership. (Don’t forget, AARP Members can get up to 50% more AARP Rewards points!)",
        },
      },
      {
        "@type": "Question",
        name: "How many AARP Rewards points can I earn playing games?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can earn a different amount of points for each game. Look for the AARP Rewards badge on the game for the point value indicator.",
        },
      },
      {
        "@type": "Question",
        name: "How often can I earn AARP Rewards points playing games?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "You can earn points once a day for each game.",
        },
      },
      {
        "@type": "Question",
        name: "Why do certain games qualify for AARP Rewards points and others don’t?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AARP Rewards awards users for staying active, supporting strong brain health, and creating social connections. Games badged for AARP Rewards reflect this ideal.",
        },
      },
      {
        "@type": "Question",
        name: "What is the difference between my game score and AARP Rewards points?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Your game score is what you achieve by playing any game on games.aarp.org. Each time you play a game, you will see your score when you reach the game end screen. Your daily and personal best scores will also show in your personal score box below the game when you’re logged in. If you qualify for the game’s leaderboard, you will see your username and the best daily, weekly or monthly score there, too. These scores help you achieve your personal best, but do not equate to any monetary value. AARP Rewards points are points that you earn by completing AARP Rewards activities throughout AARP.org, including games marked with the AARP Rewards badge. When you’re logged in and AARP Rewards activated, your total AARP Rewards points are displayed in the red bar at the top of AARP.org. You can redeem these points towards points-only offers, sweepstakes, discounted gift cards and more at AARP.org/Rewards.",
        },
      },
      {
        "@type": "Question",
        name: "How do I earn AARP Rewards points on games?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "To earn AARP Rewards points on games, you must first be registered on aarp.org and activated on AARP Rewards. Then, play and complete a game under the AARP Rewards category on AARP Games.Once you complete the crossword puzzle, click “Submit Score” to submit your game and earn points. Remember, you can only earn points once for each game per day. Give it your best shot and aim for a top score! Keep in mind that using the puzzles’ hint features in the menu, like the ‘Reveal’ function or the ‘Show Errors’ toggle in the ‘on’ position, could affect your score. Don’t forget, to get your AARP Rewards points, you must hit ‘Submit Score’ when you reach the end of the game or quit the game from the menu, instead of browsing out. The points that you earn from these games also count towards your AARP Reward point daily limits.",
        },
      },
    ],
    publisher: {
      "@type": "Organization",
      name: "AARP",
      description:
        "AARP is a nonprofit, nonpartisan organization that empowers people to choose how they live as they age.",
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.aarp.net/content/dam/aarp/graphics/aarp_logos/aarp-white-logo-footer.svg",
      },
      sameAs: [
        "https://www.facebook.com/AARP/",
        "https://twitter.com/AARP/",
        "https://www.instagram.com/aarp/",
        "https://www.linkedin.com/company/aarp/",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        telephone: "1 (888) 687-2277",
        url: "https://www.aarp.org/about-aarp/contact-us/",
      },
    },
    dateModified: newDate,
  };
};

export const categoryPageStructuredData = (
  currentURL: string,
  metaTitle: string,
  metaDescription: string,
  gameList: any,
  newDate: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: currentURL,
    name: metaTitle,
    isPartOf: "AARP Games",
    description: metaDescription,
    mainEntity: [
      {
        "@type": "ItemList",

        itemListElement: [
          gameList.map((elem, index) => {
            return {
              "@type": "ListItem",
              position: index + 1,
              name: elem.name ? elem.name : elem.seo_GameName,
              item: elem.seo_FinalURL,
            };
          }),
        ],
      },
    ],
    publisher: {
      "@type": "Organization",
      name: "AARP",
      description:
        "AARP is a nonprofit, nonpartisan organization that empowers people to choose how they live as they age.",
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.aarp.net/content/dam/aarp/graphics/aarp_logos/aarp-white-logo-footer.svg",
      },
      sameAs: [
        "https://www.facebook.com/AARP/",
        "https://twitter.com/AARP/",
        "https://www.instagram.com/aarp/",
        "https://www.linkedin.com/company/aarp/",
      ],
    },
    dateModified: newDate,
  };
};

export const gameDetailPageStructuredData = (
  currentURL: string,
  metaTitle: string,
  metaDescription: string,
  game: any,
  newDate: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: currentURL,
    name: metaTitle,
    isPartOf: "AARP Games",
    description: metaDescription,
    mainEntity: [
      {
        "@type": "VideoGame",
        additionalType: "WebApplication",
        name: game.name,
        playMode: "SinglePlayer",
        gamePlatform: ["Mobile game", "Mac game", "PC game", "Online game"],
        publisher: "AARP",
        genre: game.categories,
        inLanguage: ["English"],
        image: checkFullPath(game.assetOriginUrl, game.screenshotPath),
        thumbnailURL: checkFullPath(game.assetOriginUrl, game.secondaryThumbPath),
        applicationCategory: "GameApplication",
        operatingSystem: ["Android", "macOS", "iOS", "Windows", "Unix"],
        description: game.seo_OnPageDescription,
        offers: {
          "@type": "Offer",
          availability: "http://schema.org/InStock",
          price: "0",
          priceCurrency: "USD",
        },
      },
    ],
    publisher: {
      "@type": "Organization",
      name: "AARP",
      description:
        "AARP is a nonprofit, nonpartisan organization that empowers people to choose how they live as they age.",
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.aarp.net/content/dam/aarp/graphics/aarp_logos/aarp-white-logo-footer.svg",
      },
      sameAs: [
        "https://www.facebook.com/AARP/",
        "https://twitter.com/AARP/",
        "https://www.instagram.com/aarp/",
        "https://www.linkedin.com/company/aarp/",
      ],
    },
    dateModified: newDate,
  };
};
